/**
 * Single source of truth for the support contact email.
 *
 * Resolves once at module load. `VITE_CONTACT_EMAIL` is the canonical env
 * override (already wired through `.github/workflows/deploy.yml` per
 * environment); the hardcoded fallback is the public address users see
 * everywhere `VITE_CONTACT_EMAIL` is unset.
 *
 * Consumers should NEVER inline a mailto address — always import either
 * `SUPPORT_EMAIL` or `supportMailto()` from this module. That keeps the
 * address in one place if it ever changes (the previous landscape had
 * `admin@`, `paul@`, and `support@` all in flight simultaneously).
 */
export const SUPPORT_EMAIL =
  (import.meta.env.VITE_CONTACT_EMAIL as string | undefined) ??
  'support@arcanumhq.com';

interface SupportMailtoOpts {
  subject?: string;
  body?: string;
}

/** Build a `mailto:` href with optional URL-encoded subject + body prefills. */
export function supportMailto({
  subject,
  body,
}: SupportMailtoOpts = {}): string {
  const params: string[] = [];
  if (subject) params.push('subject=' + encodeURIComponent(subject));
  if (body) params.push('body=' + encodeURIComponent(body));
  return `mailto:${SUPPORT_EMAIL}${
    params.length ? '?' + params.join('&') : ''
  }`;
}
